import { useLazyQuery, useMutation } from '@apollo/client';
import React, { useCallback, useEffect, useState } from 'react';
import { Accordion, Alert, Badge, Button, Card, Form, InputGroup, Spinner, Table } from 'react-bootstrap';
import {
  ADMIN_BLOCK_USER_FRIEND_RELATION,
  ADMIN_FORCE_ADD_FRIEND_RELATION,
  ADMIN_UNBLOCK_USER_FRIEND_RELATION,
  ADMIN_USER_FRIEND_RELATIONS,
} from '../../../graphql';

type FriendRelation = {
  id: string;
  fromId: string;
  toId: string;
  rawState: string;
  displayStatus: string;
  opponent: {
    id: string;
    displayName: string;
    picture?: string | null;
  };
};

type FriendRelationsPayload = {
  id: string;
  hasMore: boolean;
  totalCount: number;
  list: FriendRelation[];
};

type FriendRelationsQuery = {
  adminUserFriendRelations: FriendRelationsPayload;
};

type FriendRelationsQueryVariables = {
  input: {
    userId: string;
    opponentUserId?: string;
    limit?: number;
    offset?: number;
  };
};

type AdminUnblockUserFriendRelationMutation = {
  adminUnblockUserFriendRelation: {
    success: boolean;
  };
};

type AdminUnblockUserFriendRelationMutationVariables = {
  input: {
    userId: string;
    opponentUserId: string;
  };
};

type AdminBlockUserFriendRelationMutation = {
  adminBlockUserFriendRelation: {
    success: boolean;
  };
};

type AdminBlockUserFriendRelationMutationVariables = {
  input: {
    userId: string;
    opponentUserId: string;
  };
};

type AdminForceAddFriendRelationMutation = {
  adminForceAddFriendRelation: {
    success: boolean;
  };
};

type AdminForceAddFriendRelationMutationVariables = {
  input: {
    userId: string;
    opponentUserId: string;
  };
};

type Props = {
  userId: string;
};

const PAGE_SIZE = 10;

const getBadgeVariant = (displayStatus: string) => {
  if (displayStatus === 'Blocked') {
    return 'danger';
  }
  if (displayStatus.startsWith('Pending')) {
    return 'warning';
  }
  if (displayStatus === 'Normal') {
    return 'success';
  }
  return 'secondary';
};

const getRelationDetails = (relation: FriendRelation, currentUserId: string) => {
  const isStartedByCurrentUser = relation.fromId === currentUserId;

  switch (relation.rawState) {
    case 'BLOCKED':
      return isStartedByCurrentUser ? 'You blocked this user' : 'Blocked you';
    case 'BLOCKED_FROM':
      return isStartedByCurrentUser ? 'You blocked this user' : 'Blocked you';
    case 'BLOCKED_TO':
      return isStartedByCurrentUser ? 'You blocked this user' : 'Blocked you';
    case 'PENDING':
      return isStartedByCurrentUser ? 'You sent a friend request' : 'Sent you a friend request';
    case 'FRIEND':
      return 'Friends';
    case 'NEUTRAL':
    default:
      return '-';
  }
};

export default function FriendListDisplay({ userId }: Props) {
  const [expanded, setExpanded] = useState(false);
  const [searchUserId, setSearchUserId] = useState('');
  const [relations, setRelations] = useState<FriendRelation[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [searchMode, setSearchMode] = useState(false);
  const [hasLoadedInitial, setHasLoadedInitial] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionRelationId, setActionRelationId] = useState<string | null>(null);

  const [fetchRelations, { loading, error }] = useLazyQuery<FriendRelationsQuery, FriendRelationsQueryVariables>(
    ADMIN_USER_FRIEND_RELATIONS,
    { fetchPolicy: 'network-only' }
  );
  const [unblockRelation] = useMutation<
    AdminUnblockUserFriendRelationMutation,
    AdminUnblockUserFriendRelationMutationVariables
  >(ADMIN_UNBLOCK_USER_FRIEND_RELATION);
  const [blockRelation] = useMutation<
    AdminBlockUserFriendRelationMutation,
    AdminBlockUserFriendRelationMutationVariables
  >(ADMIN_BLOCK_USER_FRIEND_RELATION);
  const [forceAddFriendRelation] = useMutation<
    AdminForceAddFriendRelationMutation,
    AdminForceAddFriendRelationMutationVariables
  >(ADMIN_FORCE_ADD_FRIEND_RELATION);

  const loadRelations = useCallback(
    async ({
      opponentUserId,
      offset = 0,
      append = false,
    }: {
      opponentUserId?: string;
      offset?: number;
      append?: boolean;
    }) => {
      if (!append) {
        setHasLoadedInitial(true);
      }

      const { data } = await fetchRelations({
        variables: {
          input: {
            userId,
            opponentUserId,
            limit: opponentUserId ? 1 : PAGE_SIZE,
            offset: opponentUserId ? 0 : offset,
          },
        },
      });

      const payload = data?.adminUserFriendRelations;
      if (!payload) {
        setRelations([]);
        setHasMore(false);
        return;
      }

      setRelations((current) => (append ? [...current, ...payload.list] : payload.list));
      setHasMore(payload.hasMore);
      setSearchMode(Boolean(opponentUserId));
    },
    [fetchRelations, userId]
  );

  useEffect(() => {
    setRelations([]);
    setHasMore(false);
    setSearchMode(false);
    setSearchUserId('');
    setHasLoadedInitial(false);
    setActionError(null);
    setActionRelationId(null);
  }, [userId]);

  useEffect(() => {
    if (expanded && !hasLoadedInitial && !loading) {
      loadRelations({}).catch(() => undefined);
    }
  }, [expanded, hasLoadedInitial, loadRelations, loading]);

  const handleSearch = async () => {
    const opponentUserId = searchUserId.trim();
    if (!opponentUserId) {
      await loadRelations({});
      return;
    }
    await loadRelations({ opponentUserId });
  };

  const handleReset = async () => {
    setSearchUserId('');
    await loadRelations({});
  };

  const handleLoadMore = async () => {
    await loadRelations({ offset: relations.length, append: true });
  };

  const handleSearchClick = () => {
    handleSearch().catch(() => undefined);
  };

  const handleResetClick = () => {
    handleReset().catch(() => undefined);
  };

  const handleLoadMoreClick = () => {
    handleLoadMore().catch(() => undefined);
  };

  const handleUnblockClick = async (relation: FriendRelation) => {
    setActionError(null);
    setActionRelationId(relation.id);
    try {
      await unblockRelation({
        variables: {
          input: {
            userId,
            opponentUserId: relation.opponent.id,
          },
        },
      });

      if (searchMode && searchUserId.trim()) {
        await loadRelations({ opponentUserId: searchUserId.trim() });
      } else {
        await loadRelations({});
      }
    } catch {
      setActionError('Failed to unblock relation');
    } finally {
      setActionRelationId(null);
    }
  };

  const handleBlockClick = async (relation: FriendRelation) => {
    setActionError(null);
    setActionRelationId(relation.id);
    try {
      await blockRelation({
        variables: {
          input: {
            userId,
            opponentUserId: relation.opponent.id,
          },
        },
      });

      if (searchMode && searchUserId.trim()) {
        await loadRelations({ opponentUserId: searchUserId.trim() });
      } else {
        await loadRelations({});
      }
    } catch {
      setActionError('Failed to block relation');
    } finally {
      setActionRelationId(null);
    }
  };

  const handleForceAddFriendClick = async (relation: FriendRelation) => {
    setActionError(null);
    setActionRelationId(relation.id);
    try {
      await forceAddFriendRelation({
        variables: {
          input: {
            userId,
            opponentUserId: relation.opponent.id,
          },
        },
      });

      if (searchMode && searchUserId.trim()) {
        await loadRelations({ opponentUserId: searchUserId.trim() });
      } else {
        await loadRelations({});
      }
    } catch {
      setActionError('Failed to force add friend relation');
    } finally {
      setActionRelationId(null);
    }
  };

  return (
    <Card className="mb-2 shadow-sm border-0">
      <Card.Body className="py-2">
        <Accordion>
          <Accordion.Toggle as={Card.Title} variant="link" eventKey="0" onClick={() => setExpanded((value) => !value)}>
            <div style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Friend List</span>
              <Button variant="link">
                {!expanded && <i className="fas fa-chevron-down" />}
                {expanded && <i className="fas fa-chevron-up" />}
              </Button>
            </div>
          </Accordion.Toggle>
          <Accordion.Collapse eventKey="0">
            <div>
              <p className="text-muted small mb-2">
                <i>
                  Review this user&apos;s friend relationship status, or jump straight to a
                  specific opponent or friend by user ID.
                </i>
              </p>

              <div
                className="mb-3 p-3"
                style={{
                  border: '1px solid #dbe4ff',
                  borderRadius: 14,
                  background: 'linear-gradient(135deg, #f8fbff 0%, #eef4ff 100%)',
                }}
              >
                <div className="d-flex justify-content-between align-items-center flex-wrap mb-2">
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#2f5bea' }}>Quick Lookup</div>
                    <div className="text-muted small">
                      Search a specific opponent or friend user ID without scanning the full list.
                    </div>
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: '#4c5a7a',
                      background: '#ffffff',
                      border: '1px solid #d7def2',
                      borderRadius: 999,
                      padding: '6px 10px',
                    }}
                  >
                    {loading ? 'Loading...' : `${relations.length} rows loaded`}
                  </div>
                </div>

                <InputGroup size="sm" style={{ maxWidth: 560 }}>
                  <Form.Control
                    placeholder="Enter opponent/friend user ID"
                    value={searchUserId}
                    onChange={(event) => setSearchUserId(event.target.value)}
                    onKeyPress={(event: React.KeyboardEvent<HTMLInputElement>) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        handleSearchClick();
                      }
                    }}
                    style={{
                      borderTopLeftRadius: 10,
                      borderBottomLeftRadius: 10,
                      minHeight: 42,
                    }}
                  />
                  <Button
                    variant="primary"
                    onClick={handleSearchClick}
                    disabled={loading}
                    style={{ minWidth: 96, fontWeight: 600, marginRight: 8 }}
                  >
                    Search
                  </Button>
                  <Button
                    variant="light"
                    onClick={handleResetClick}
                    disabled={loading}
                    style={{
                      minWidth: 88,
                      fontWeight: 600,
                      borderColor: '#cfd7ea',
                      color: '#4f5d7a',
                    }}
                  >
                    Reset
                  </Button>
                </InputGroup>
              </div>

              <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="text-muted small">
                  {searchMode
                    ? 'Showing filtered result for the searched opponent.'
                    : 'Showing paginated relation history for this user.'}
                </div>
              </div>

              {error && (
                <Alert variant="danger" className="py-1 mb-2">
                  Failed to load friend relations
                </Alert>
              )}
              {actionError && (
                <Alert variant="danger" className="py-1 mb-2">
                  {actionError}
                </Alert>
              )}

              {loading && !relations.length ? (
                <div className="py-3 text-center">
                  <Spinner animation="border" size="sm" />
                </div>
              ) : relations.length > 0 ? (
                <>
                  <div className="table-responsive">
                    <Table bordered hover size="sm" className="mb-0">
                      <thead>
                        <tr>
                          <th className="text-center">Opponent/Friend User ID</th>
                          <th className="text-center">Opponent Name</th>
                          <th className="text-center">Status</th>
                          <th className="text-center">Raw State</th>
                          <th className="text-center">Details</th>
                          <th className="text-center">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {relations.map((relation) => (
                          <tr key={relation.id}>
                            <td className="text-center">{relation.opponent.id}</td>
                            <td className="text-center">{relation.opponent.displayName}</td>
                            <td className="text-center">
                              <Badge variant={getBadgeVariant(relation.displayStatus)}>{relation.displayStatus}</Badge>
                            </td>
                            <td className="text-center">{relation.rawState}</td>
                            <td className="text-center">
                              {getRelationDetails(relation, userId)}
                            </td>
                            <td className="text-center">
                              {relation.displayStatus === 'Blocked' ? (
                                <Button
                                  size="sm"
                                  variant="success"
                                  disabled={actionRelationId === relation.id}
                                  onClick={() => {
                                    handleUnblockClick(relation).catch(() => undefined);
                                  }}
                                >
                                  {actionRelationId === relation.id ? 'Updating...' : 'Unblock'}
                                </Button>
                              ) : relation.rawState === 'FRIEND' ? (
                                <div className="d-flex justify-content-center">
                                  <Button
                                    size="sm"
                                    variant="danger"
                                    disabled={actionRelationId === relation.id}
                                    onClick={() => {
                                      handleBlockClick(relation).catch(() => undefined);
                                    }}
                                  >
                                    {actionRelationId === relation.id ? 'Updating...' : 'Block'}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="secondary"
                                    className="ml-2"
                                    disabled={actionRelationId === relation.id}
                                    onClick={() => {
                                      handleUnblockClick(relation).catch(() => undefined);
                                    }}
                                  >
                                    {actionRelationId === relation.id ? 'Updating...' : 'Remove Friend'}
                                  </Button>
                                </div>
                              ) : (
                                <div className="d-flex justify-content-center">
                                  <Button
                                    size="sm"
                                    variant="danger"
                                    disabled={actionRelationId === relation.id}
                                    onClick={() => {
                                      handleBlockClick(relation).catch(() => undefined);
                                    }}
                                  >
                                    {actionRelationId === relation.id ? 'Updating...' : 'Block'}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="primary"
                                    className="ml-2"
                                    disabled={actionRelationId === relation.id}
                                    onClick={() => {
                                      handleForceAddFriendClick(relation).catch(() => undefined);
                                    }}
                                  >
                                    {actionRelationId === relation.id ? 'Updating...' : 'Force Add Friend'}
                                  </Button>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>

                  {!searchMode && hasMore && (
                    <div className="d-flex justify-content-center mt-3">
                      <Button variant="outline-primary" onClick={handleLoadMoreClick} disabled={loading}>
                        {loading ? 'Loading...' : 'Load More'}
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                <p className="text-muted mb-0">
                  <i>{searchMode ? 'No relation found for that opponent user ID.' : 'No friend relations found.'}</i>
                </p>
              )}
            </div>
          </Accordion.Collapse>
        </Accordion>
      </Card.Body>
    </Card>
  );
}
